/**
 * SensiChat - Core chat interface component for Sensi AI Assistant
 * Handles message display, input, and loading states.
 * Supports get-started mode (mode="get_started") for solo onboarding.
 */

import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Icons } from '../../lib/Icons';
import SensiMessage from './SensiMessage';
import SensiActivityCard from './SensiActivityCard';
import GetStartedFlow from './GetStartedFlow';

// Initialize functions with region
const functions = getFunctions(undefined, 'europe-west3');
const sensiChatFunc = httpsCallable(functions, 'sensiChatFunc');
const updateSoloGetStartedFunc = httpsCallable(functions, 'updateSoloGetStartedFunc');

const GET_STARTED_WELCOME = "Hi! Let's get you set up so I can point you to the right resources. What best describes you?";

const SensiChat = ({ isExpanded = true, onClose, mode = 'normal', onGetStartedComplete, onDismissGetStarted }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isGetStartedMode = mode === 'get_started';

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Welcome message: get-started vs normal
  useEffect(() => {
    if (messages.length === 0 && !isGetStartedMode) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm Sensi, your SEN guidance assistant. 👋

I can help you with:
• **UK SEND statutory guidance** - EHCP timelines, annual reviews, appeals
• **SENCO best practices** - graduated response, working with agencies
• **In-depth articles** - EBSA, PDA, difficult conversations, classroom strategies
• **Finding activities** - I can suggest resources from the app

What can I help you with today?`,
        timestamp: new Date().toISOString(),
      }]);
    }
    if (messages.length === 0 && isGetStartedMode) {
      setMessages([{
        role: 'assistant',
        content: GET_STARTED_WELCOME,
        timestamp: new Date().toISOString(),
      }]);
    }
  }, [isGetStartedMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Add user message immediately
    const userMessage = {
      role: 'user',
      content: trimmedInput,
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Prepare conversation history (exclude welcome message)
      const conversationHistory = messages
        .filter(m => m.role !== 'system')
        .slice(-10) // Last 10 messages
        .map(({ role, content }) => ({ role, content }));

      // Call the Sensi chat function - only include conversationId if it exists
      const payload = {
        message: trimmedInput,
        conversationHistory,
      };
      if (conversationId) {
        payload.conversationId = conversationId;
      }
      
      const result = await sensiChatFunc(payload);

      const response = result.data;

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        citations: response.citations,
        suggestedActivities: response.suggestedActivities,
        suggestedArticles: response.suggestedArticles,
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Save conversation ID for continuity
      if (response.conversationId) {
        setConversationId(response.conversationId);
      }

    } catch (err) {
      console.error('Sensi chat error:', err);
      setError('Sorry, I had trouble processing that. Please try again.');
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try asking again, or rephrase your question.",
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Example questions for quick access
  const exampleQuestions = [
    "How do I support a child with EBSA?",
    "How do I run an annual review?",
    "What strategies work for PDA?",
    "What is quality first teaching?",
    "When should I involve my SENCO?",
  ];

  const handleExampleClick = (question) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const handleDismissGetStarted = async () => {
    try {
      await updateSoloGetStartedFunc({ dismissedGetStarted: true, incrementLoginCount: true });
    } catch (err) {
      console.error('Dismiss get-started error:', err);
    }
    onDismissGetStarted?.();
  };

  const handleGetStartedComplete = () => {
    onGetStartedComplete?.();
  };

  const addGetStartedMessage = (msg) => {
    setMessages((prev) => [...prev, { ...msg, timestamp: msg.timestamp || new Date().toISOString() }]);
  };

  if (!isExpanded) return null;

  return (
    <div className="flex flex-col h-full bg-slate-900 rounded-2xl overflow-hidden border border-slate-700/50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600/20 to-purple-600/20 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Icons.Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Sensi</h3>
            <p className="text-xs text-slate-400">SEN Guidance Assistant</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors text-slate-400 hover:text-white"
          >
            <Icons.XMark className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <SensiMessage key={index} message={message} />
        ))}

        {/* Get-started flow: current step UI (choices or recommendations) */}
        {isGetStartedMode && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-1">
              <Icons.Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <GetStartedFlow
                addMessage={addGetStartedMessage}
                messages={messages}
                onComplete={handleGetStartedComplete}
                onDismiss={onDismissGetStarted ? handleDismissGetStarted : undefined}
              />
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Icons.Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="bg-slate-800 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Example questions (show when few messages, normal mode only) */}
        {!isGetStartedMode && messages.length <= 1 && !isLoading && (
          <div className="mt-4">
            <p className="text-xs text-slate-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(question)}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border border-slate-700/50"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Input (hidden when in get-started mode) */}
      {!isGetStartedMode && (
      <form onSubmit={handleSubmit} className="p-4 border-t border-slate-700/50">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Sensi about SEN guidance..."
              rows={1}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-all"
              style={{ minHeight: '48px', maxHeight: '120px' }}
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="p-3 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed hover:from-violet-600 hover:to-purple-700 transition-all shadow-lg shadow-violet-500/25"
          >
            <Icons.PaperAirplane className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2 text-center">
          Sensi provides guidance based on UK SEND law. Always verify statutory advice with official sources.
        </p>
      </form>
      )}
    </div>
  );
};

export default SensiChat;

